/**
 * Chat search operators — the `from:` / `in:` / `before:` / `after:` / `has:`
 * syntax a user can type straight into the search box (the Slack / Discord
 * convention), parsed into the structured filters the search routes take.
 *
 * Parsing is client-side only: the server never sees operator text, it sees
 * `from`, `has`, `since`, `until` parameters validated against the same enum
 * this file imports. Lives here, beside the search policy, so the webapp and
 * any future client agree on one grammar, and so it can be unit-tested with no
 * React in sight.
 */

import { type ChatSearchHasFilter, isChatSearchHasFilter } from './chat-search-policy';

/** The result of pulling operators out of a search box's text. */
export interface ParsedChatSearchQuery {
  /** The free text that remains once every recognized operator is removed. */
  text: string;
  /** `from:` — a sender's username, with a leading "@" dropped. */
  from?: string;
  /** `in:` — a conversation name, with a leading "#" dropped. */
  in?: string;
  /** `after:YYYY-MM-DD` — the first day to include. */
  after?: string;
  /** `before:YYYY-MM-DD` — the first day to exclude. */
  before?: string;
  /** `has:file` / `has:image` / `has:link`. */
  has?: ChatSearchHasFilter;
}

const OPERATORS = ['from', 'in', 'after', 'before', 'has'] as const;
type Operator = (typeof OPERATORS)[number];

const OPERATOR_SET: ReadonlySet<string> = new Set(OPERATORS);

/** A calendar day, `YYYY-MM-DD`, that exists (`2030-02-30` is not one). */
const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isChatSearchDay(value: string): boolean {
  const match = DAY_PATTERN.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  // Day-of-month check through the UTC calendar, so leap years are honored.
  return new Date(Date.UTC(y, m - 1, d)).getUTCDate() === d;
}

/**
 * Splits the raw text into whitespace-separated tokens, keeping a
 * double-quoted operator value together: `from:"Will Jones"` is one token.
 * Quotes elsewhere are left exactly as typed — they belong to the search text.
 */
function tokenize(raw: string): string[] {
  const tokens: string[] = [];
  const pattern = /\S*?:"[^"]*"|\S+/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(raw)) !== null) tokens.push(match[0]);
  return tokens;
}

function stripQuotes(value: string): string {
  return value.startsWith('"') && value.endsWith('"') && value.length >= 2
    ? value.slice(1, -1)
    : value;
}

/**
 * Pulls the recognized operators out of a search box's text.
 *
 * Grammar: `operator:value`, where `value` is a bare word or a `"quoted
 * string"`. Case-insensitive operator names. The LAST occurrence of a repeated
 * operator wins. An operator with an empty value, an unknown `has:` value, or
 * an `after:` / `before:` that is not a real calendar day is NOT an operator —
 * the token stays in the text, so a message about `has:video` is still
 * searchable and a half-typed date never silently narrows the search.
 */
export function parseChatSearchQuery(raw: string): ParsedChatSearchQuery {
  const parsed: ParsedChatSearchQuery = { text: '' };
  const textTokens: string[] = [];

  for (const token of tokenize(raw)) {
    const colon = token.indexOf(':');
    const name = colon > 0 ? token.slice(0, colon).toLowerCase() : '';
    const value = colon > 0 ? stripQuotes(token.slice(colon + 1)).trim() : '';

    if (!OPERATOR_SET.has(name) || value.length === 0) {
      textTokens.push(token);
      continue;
    }

    switch (name as Operator) {
      case 'from':
        parsed.from = value.replace(/^@/, '');
        break;
      case 'in':
        parsed.in = value.replace(/^#/, '');
        break;
      case 'after':
        if (!isChatSearchDay(value)) {
          textTokens.push(token);
          break;
        }
        parsed.after = value;
        break;
      case 'before':
        if (!isChatSearchDay(value)) {
          textTokens.push(token);
          break;
        }
        parsed.before = value;
        break;
      case 'has':
        if (!isChatSearchHasFilter(value.toLowerCase())) {
          textTokens.push(token);
          break;
        }
        parsed.has = value.toLowerCase() as ChatSearchHasFilter;
        break;
    }
  }

  parsed.text = textTokens.join(' ');
  return parsed;
}

/** True when the text carries at least one recognized operator. */
export function hasChatSearchOperators(parsed: ParsedChatSearchQuery): boolean {
  return (
    parsed.from !== undefined ||
    parsed.in !== undefined ||
    parsed.after !== undefined ||
    parsed.before !== undefined ||
    parsed.has !== undefined
  );
}
